import { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { ctx } from '../outline/ctx.js'
import { wf } from '../outline/state.js'
import { moveMilestoneToPhase } from '../services/milestones.js'
import { getHubEntriesForMilestone, upsertEntry } from '../services/journey.js'

export default function MoveToPhaseModal() {
  const [open, setOpen]               = useState(false)
  const [msId, setMsId]               = useState(null)
  const [currentPhId, setCurrentPhId] = useState(null)
  const [selectedPhId, setSelectedPhId] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError]   = useState('')

  useEffect(() => {
    function onOpen(e) {
      setMsId(e.detail.msId)
      setCurrentPhId(e.detail.currentPhId || null)
      setSelectedPhId('')
      setSaving(false)
      setError('')
      setOpen(true)
    }
    window.addEventListener('open-move-phase-modal', onOpen)
    return () => window.removeEventListener('open-move-phase-modal', onOpen)
  }, [])

  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape' && open) close() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open])

  function close() { setOpen(false); setMsId(null) }

  const ms       = msId ? (ctx.state?.milestones || []).find(m => m.id === msId) : null
  const msName   = ms?.fields['Full Deliverable Name'] || ms?.fields['Name'] || 'Milestone'
  const phases   = ctx.state?.phases || []
  const otherPhases = phases.filter(ph => ph.id !== currentPhId)

  // A distinguishable label per phase — same fields the outline uses, so the three "Additional
  // Services" (and Long Distance Travel) phases read apart. `tag` is the phase code suffix (AS2, LDT1, CD).
  function phaseLabel(ph) {
    const f = ph.fields || {}
    const base = f['Phase Name'] || f['Name'] || 'Phase'
    const code = f['Project Phase ID'] || ''                 // e.g. "25-03-AS2"
    const tag = code.indexOf('-') !== -1 ? code.split('-').slice(2).join('-') : ''
    const title = f['Custom Title'] || ''
    const asNum = f['Additional Services Number']
    const ldtNum = f['Long Distance Travel Number']
    const ldtDate = f['Long Distance Travel Date']
    let main = base
    if (asNum != null && asNum !== '') main = 'Additional Services' + (title ? ' — ' + title : ' #' + asNum)
    else if (ldtNum != null && ldtNum !== '') main = 'Long Distance Travel' + (ldtDate ? ' (' + ldtDate + ')' : ' #' + ldtNum)
    return { main, tag }
  }

  async function handleMove() {
    if (!selectedPhId || saving) return
    setSaving(true); setError('')

    const newPh     = otherPhases.find(p => p.id === selectedPhId)
    const newPhName = newPh?.fields['Phase Name'] || ''

    try {
      const res = await moveMilestoneToPhase(msId, selectedPhId, newPhName)
      if (res.error) throw new Error(res.error)
      /* Update in-memory so render re-buckets correctly */
      if (ms) { ms.fields['Project Phase'] = newPhName; ms.fields['Phase'] = [selectedPhId] }
      // The milestone's own Client Hub card follows it live; the sketch ribbons and PDF documents
      // filed under it carry their own phase, so they move with it here (best-effort).
      const projId = ctx.recordId || wf.zoomProjectId
      if (projId && newPhName) {
        getHubEntriesForMilestone(projId, msId)
          .then(({ journeyId, entries }) => Promise.all(entries
            .filter(en => (en.data || {}).phase !== newPhName)
            .map(en => upsertEntry({ id: en.id, journeyId, data: { ...(en.data || {}), phase: newPhName } }))))
          .then(() => window.dispatchEvent(new CustomEvent('journey-updated')))
          .catch(() => {})
      }
      close()
      window.dispatchEvent(new CustomEvent('outline-render'))
    } catch {
      setSaving(false)
      setError('Move failed — please try again.')
    }
  }

  if (!open) return null

  const btnBase = {
    border: 'none', borderRadius: 4, padding: '8px 18px',
    fontFamily: '"DM Sans",sans-serif', fontSize: 13, fontWeight: 500, cursor: 'pointer'
  }

  return createPortal(
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)',
        zIndex: 9000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16
      }}
    >
      <div data-modal-id="MoveToPhaseModal" style={{
        background: 'var(--aj-bg-elevated)', borderRadius: 10, border: '0.5px solid var(--aj-border)',
        padding: '22px 24px', width: 360, maxWidth: '100%',
        boxShadow: '0 8px 40px rgba(0,0,0,0.18)'
      }}>
        {/* Title */}
        <div style={{
          fontFamily: '"DM Sans",sans-serif', fontWeight: 500, fontSize: 12,
          letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--aj-text)', marginBottom: 16
        }}>
          Move milestone
        </div>

        {/* Milestone name callout */}
        <div style={{
          fontFamily: '"DM Sans",sans-serif', fontSize: 13, color: 'var(--aj-text)',
          padding: '8px 10px', background: 'var(--aj-bg-subtle)', borderRadius: 4,
          fontWeight: 500, marginBottom: 16
        }}>
          {msName}
        </div>

        {/* Phase label */}
        <div style={{
          fontFamily: '"DM Sans",sans-serif', fontSize: 11, fontWeight: 500,
          letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--aj-text-muted)', marginBottom: 8
        }}>
          Move to phase
        </div>

        {/* Phase radio list */}
        {otherPhases.length === 0 ? (
          <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 13, color: 'var(--aj-text-muted)', padding: '4px 0' }}>
            No other phases available.
          </div>
        ) : otherPhases.map(ph => {
          const { main, tag } = phaseLabel(ph)
          const isChecked = selectedPhId === ph.id
          return (
            <label
              key={ph.id}
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '7px 8px', cursor: 'pointer', borderRadius: 4,
                fontFamily: '"DM Sans",sans-serif', fontSize: 13, color: 'var(--aj-text)',
                border: `0.5px solid ${isChecked ? 'var(--aj-border)' : 'transparent'}`,
                background: isChecked ? 'var(--aj-bg-subtle)' : '',
                marginBottom: 4, transition: 'background 0.08s, border-color 0.08s'
              }}
            >
              <input
                type="radio" name="move-phase-radio" value={ph.id}
                checked={isChecked} onChange={() => setSelectedPhId(ph.id)}
                style={{ flexShrink: 0, accentColor: 'var(--aj-text)', cursor: 'pointer' }}
              />
              {tag && (
                <span style={{
                  flexShrink: 0, fontFamily: '"DM Mono",monospace', fontSize: 10, color: 'var(--aj-text-mid)',
                  background: 'var(--aj-bg-muted)', borderRadius: 4, padding: '1px 6px',
                }}>{tag}</span>
              )}
              <span>{main}</span>
            </label>
          )
        })}

        {/* Footer */}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', alignItems: 'center', marginTop: 20 }}>
          {error && (
            <span style={{ fontSize: 12, color: '#c0392b', marginRight: 'auto' }}>{error}</span>
          )}
          <button onClick={close} style={{
            ...btnBase, background: 'transparent',
            border: '0.5px solid var(--aj-border-mid)', color: 'var(--aj-text-mid)'
          }}>
            Cancel
          </button>
          <button
            onClick={handleMove}
            disabled={!selectedPhId || saving}
            style={{
              ...btnBase, background: '#07272D', color: 'white',
              opacity: !selectedPhId || saving ? 0.5 : 1
            }}
          >
            {saving ? 'Moving…' : 'Move'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}
